import React, { useState, useRef, useCallback } from 'react'; // useRef aur useCallback ko import karein
import { useLocation } from 'react-router-dom';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { EventClickArg, formatDate } from '@fullcalendar/core';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import ListGroup from 'react-bootstrap/ListGroup';
import Badge from 'react-bootstrap/Badge';
import { DatesSetArg } from '@fullcalendar/core';
import axios from 'axios';
import secureLocalStorage from 'react-secure-storage';
import { toast } from 'react-toastify';
import { useMutation } from 'react-query';
import { useGeneralSettings } from '../../../../app/contexts/GeneralSettingsContext';
import { convertDateFormatToOptions, convertTimeFormatToOptions, formatDateTimeCustom, formatDynamicDateTime } from '../../../../app/helper-functions/DateFunction';
import momentTimezonePlugin from '@fullcalendar/moment-timezone';
type CalendarEvent = {
  id: string;
  title: string;
  start: string;
  end?: string;
  allDay?: boolean;
  extendedProps: {
    type: 'Meeting' | 'Appointment' | 'Task' | 'Deadline' | 'Training';
    description?: string;
    completed?: boolean;
    host?: string;
    client?: string;
    location?: string;
    platform?: string;
    slot_id?: number;
    status?: string;
    location_type?: string;
    appointment_status?: string;
  };
};

type MoreEventsModalProps = {
  show: boolean;
  onHide: () => void;
  selectedDate: string;
  events: CalendarEvent[];
  onTaskCompletion: (eventId: string) => void;
  timeFormatOptions: Intl.DateTimeFormatOptions;
};

type EventDetailsModalProps = {
  show: boolean;
  onHide: () => void;
  event: CalendarEvent | null;
  onStatusChange: (params: { id: string; status: string }) => void;
  dateFormat: string;
  timeFormat: string;
  timeZone: string;
  isClosing: boolean;
};

const MoreEventsModal: React.FC<MoreEventsModalProps> = ({ show, onHide, selectedDate, events, onTaskCompletion, timeFormatOptions }) => {
  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Tasks for {selectedDate}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <ListGroup>
          {events.map((event) => (
            <ListGroup.Item
              key={`${event.id}-${event.extendedProps.slot_id || event.start}`}
              as="li"
              className="d-flex justify-content-between align-items-start"
            >
              <div className="ms-2 me-auto">
                <div className={`fw-bold ${event.extendedProps.completed ? 'text-decoration-line-through text-muted' : ''}`}>
                  {event.title}
                </div>
                <small className={event.extendedProps.completed ? 'text-decoration-line-through text-muted' : ''}>
                  {/* {formatDate(event.start, { hour: 'numeric', minute: '2-digit', meridiem: 'short' })} */}
                  {formatDate(event.start, timeFormatOptions)}
                </small>
              </div>
              {event.extendedProps.type === 'Task' && (
                <Button
                  variant={event.extendedProps.completed ? "outline-secondary" : "outline-success"}
                  size="sm"
                  onClick={() => onTaskCompletion(event.id)}
                >
                  {event.extendedProps.completed ? 'Undo' : 'Complete'}
                </Button>
              )}
            </ListGroup.Item>
          ))}
        </ListGroup>
      </Modal.Body>
    </Modal>
  );
};
const EventDetailsModal: React.FC<EventDetailsModalProps> = ({ show, onHide, event, onStatusChange, dateFormat, timeFormat, timeZone, isClosing }) => {
  const [statusLoading, setStatusLoading] = useState(false);
  if (!event) return null;

  const { title, start, end, extendedProps } = event;
  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center">
          {title}
          <Badge pill bg="info" className="ms-3">
            {extendedProps.type}
          </Badge>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {extendedProps.description && <p><strong>Description:</strong> {extendedProps.description}</p>}

        <ListGroup variant="flush">
          <ListGroup.Item>
            <strong>Time:</strong>
            {formatDateTimeCustom(start, dateFormat, timeFormat, timeZone, true)}
            {end && ` - ${formatDateTimeCustom(end, dateFormat, timeFormat, timeZone, false)}`}
            {/* {formatDate(start, {
              timeZone: "UTC", weekday: 'long', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit'
            })}
            {end && ` - ${formatDate(end, { timeZone: "UTC", hour: 'numeric', minute: '2-digit' })}`} */}
          </ListGroup.Item>

          {extendedProps.host && <ListGroup.Item><strong>Host:</strong> {extendedProps.host}</ListGroup.Item>}
          {extendedProps.client && <ListGroup.Item><strong>Client:</strong> {extendedProps.client}</ListGroup.Item>}
          {extendedProps.platform && <ListGroup.Item><strong>Platform:</strong> {extendedProps.platform}</ListGroup.Item>}
          {extendedProps.location_type && <ListGroup.Item><strong>Location Type:</strong> {extendedProps.location_type}</ListGroup.Item>}

          {extendedProps.status && (
            <ListGroup.Item className='d-flex'>
              <strong>Payment Status:</strong>
              <p className="ms-2">{extendedProps.status}</p>
            </ListGroup.Item>
          )}
          {extendedProps.appointment_status && (
            <ListGroup.Item>
              <strong>Status:</strong>
              <Badge
                bg={
                  extendedProps.appointment_status === 'open'
                    ? 'success'
                    : extendedProps.appointment_status === 'close'
                      ? 'warning'
                      : 'primary'
                }
                className="ms-2"
              >
                {extendedProps.appointment_status}
              </Badge>
            </ListGroup.Item>
          )}

        </ListGroup>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Close</Button>
        {event?.extendedProps.appointment_status === 'open' && (
          <Button
            variant="danger"
            disabled={isClosing}
            onClick={() => {
              onStatusChange({ id: event.id.split('-')[0], status: event.extendedProps.appointment_status ?? 'open' });
            }}
          >
            {isClosing ? 'Closing...' : 'Close Appointment'}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};
const FullCalendarWidget: React.FC = () => {
  const { getSettingValue } = useGeneralSettings();
  const userDateFormat = getSettingValue('date_format') || 'dd-mm-yyyy';
  const userTimeFormat = getSettingValue('time_format') || '12hr';
  const userTimeZone = getSettingValue('time_zone') || 'UTC';

  const location = useLocation();
  const state = location.state as { id: number | string; fullname?: string };
  const userId = state?.id;
  const fullname = state?.fullname;

  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [moreModalOpen, setMoreModalOpen] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedDayEvents, setSelectedDayEvents] = useState<CalendarEvent[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [calendarRange, setCalendarRange] = useState<{ start: Date; end: Date }>({
    start: new Date(),
    end: new Date()
  });

  // ===== DYNAMIC FORMATS HAASIL KAREIN AUR CONVERT KAREIN =====
  console.log("Passing to FullCalendar -> timeZone prop:", userTimeZone);
  console.log("Final 'events' array passed to FullCalendar:", events);
  const dateFormatOptions = convertDateFormatToOptions(userDateFormat, userTimeZone);
  const timeFormatOptions = convertTimeFormatToOptions(userTimeFormat, userTimeZone);
  const renderEventContent = (eventInfo: any) => {

    if (eventInfo.event.allDay) {

      return (
        <>
          <div className='fc-daygrid-event-dot'></div>
          <div className='fc-event-title'>{eventInfo.event.title}</div>
        </>
      );
    } else {

      const customTimeText = formatDateTimeCustom(
        eventInfo.event.startStr,
        userDateFormat,
        userTimeFormat,
        userTimeZone,
        false
      );


      return (
        <>
          <div className='fc-daygrid-event-dot'></div>
          <div className='fc-event-time'>{customTimeText}</div>
          <div className='fc-event-title'>{eventInfo.event.title}</div>
        </>
      );
    }
  };
  // const handleMoreLinkClick = (info: { date: Date; allSegs: any[] }) => {
  //   const dayEvents = info.allSegs.map(seg => seg.event.toPlainObject() as CalendarEvent);
  //   setSelectedDate(formatDate(info.date, { year: 'numeric', month: 'long', day: 'numeric' }));
  //   setSelectedDayEvents(dayEvents);
  //   setMoreModalOpen(true);
  //   return 'popover';
  // };
  const handleMoreLinkClick = (info: { date: Date; allSegs: any[] }) => {
    const dayEvents = info.allSegs.map(seg => seg.event.toPlainObject() as CalendarEvent);
    setSelectedDate(formatDate(info.date, dateFormatOptions)); // Dynamic format
    setSelectedDayEvents(dayEvents);
    setMoreModalOpen(true);
    return 'popover';
  };

  const handleTaskCompletion = (eventId: string) => {
    const updateEvents = (eventsList: CalendarEvent[]) =>
      eventsList.map(event =>
        event.id === eventId
          ? { ...event, extendedProps: { ...event.extendedProps, completed: !event.extendedProps.completed } }
          : event
      );
    setEvents(updateEvents);
    setSelectedDayEvents(updateEvents);
  };

  const handleEventClick = (clickInfo: EventClickArg) => {
    const event = clickInfo.event.toPlainObject() as CalendarEvent;
    setSelectedEvent(event);
    setDetailsModalOpen(true);
  };
  const fetchEventsByDateRange = async (start: Date, end: Date) => {
    if (!userId) {
      console.error("User ID not found in location state.");
      return;
    }

    setLoading(true);

    const formatDateToYMD = (date: Date) => date.toISOString().split('T')[0];

    try {
      const response = await axios.post(secureLocalStorage.getItem("baseURL") + '/api/calendar-events', {
        from_date: formatDateToYMD(start),
        to_date: formatDateToYMD(end),
        user_id: userId,
      });

      if (response.data && response.data.status && Array.isArray(response.data.data)) {
        const formattedEvents = response.data.data.map((apiEvent: any) => ({
          id: `${apiEvent.id}-${apiEvent.extendedProps.slot_id}`,
          title: apiEvent.title,
          start: apiEvent.start,
          end: apiEvent.end,
          allDay: apiEvent.allDay,
          extendedProps: {
            ...apiEvent.extendedProps,
            type: apiEvent.extendedProps.type || 'Appointment',
          },
        }));
        setEvents(formattedEvents);

      } else {
        console.error("API response is not in the expected format:", response.data);
        setEvents([]);
      }
    } catch (error) {
      console.error("Error fetching calendar events:", error);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };
  const handleDatesSet = useCallback((arg: DatesSetArg) => {
    setCalendarRange({ start: arg.start, end: arg.end });
    fetchEventsByDateRange(arg.start, arg.end);
  }, [userId]);
  const { mutate: handleStatusChange, isLoading: isStatusChanging } = useMutation(({ id, status }: { id: string; status: string }) => {
    const newStatus = status === 'open' ? 'close' : 'open';
    return axios.post(secureLocalStorage.getItem("baseURL") + '/api/change-appointment-status', {
      appointment_id: id,
      appointment_status: newStatus,
    }).then((res) => {
      toast.success(res.data.message);
      setDetailsModalOpen(false);
      if (calendarRange) {
        fetchEventsByDateRange(calendarRange.start, calendarRange.end);
      }
    }).catch((error) => {
      toast.error(error.response?.data?.message || 'Failed to update status');
    });
  });

  return (
    <div className="p-4">
      {fullname && (
        <h3 className="mb-4 text-center">
          {fullname} Calendar
        </h3>
      )}
      {loading && <div style={{ textAlign: 'center', fontSize: '1.5rem', padding: '10px' }}>Loading...</div>}

      <FullCalendar
        // eventTimeFormat={timeFormatOptions}
        // key={userTimeZone}
        eventContent={renderEventContent}
        dayHeaderFormat={{ weekday: 'short', day: 'numeric', month: 'numeric' }}
        // timeZone="UTC"
        timeZone={userTimeZone}
        // plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
        plugins={[
          dayGridPlugin,
          timeGridPlugin,
          interactionPlugin,
          momentTimezonePlugin
        ]}
        headerToolbar={{
          left: 'prev,next today',
          center: 'title',
          right: 'dayGridMonth,timeGridWeek,timeGridDay',
        }}
        initialView="dayGridMonth"
        editable={false}
        selectable={true}
        selectMirror={true}
        dayMaxEvents={5}
        moreLinkClick={handleMoreLinkClick}
        events={events}
        eventClick={handleEventClick}
        height="auto"
        datesSet={handleDatesSet}

      />

      <MoreEventsModal
        show={moreModalOpen}
        onHide={() => setMoreModalOpen(false)}
        selectedDate={selectedDate}
        events={selectedDayEvents}
        onTaskCompletion={handleTaskCompletion}
        timeFormatOptions={timeFormatOptions}
      />

      <EventDetailsModal
        show={detailsModalOpen}
        onHide={() => setDetailsModalOpen(false)}
        event={selectedEvent}
        // onStatusChange={handleStatusChange.mutate}
        onStatusChange={handleStatusChange}
        dateFormat={userDateFormat}
        timeFormat={userTimeFormat}
        timeZone={userTimeZone}
        isClosing={isStatusChanging}
      />
    </div>
  );
};

export default FullCalendarWidget;